import { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { format, addDays } from "date-fns";
import { fr } from "date-fns/locale";
import {
  LayoutDashboard, Calendar, LogOut, MapPin, Clock, User, Users,
  CheckCircle, Truck, Package, Filter, RefreshCw, Trash2, Crown, Repeat, Bot, Building, X, Eye, UserCheck, Ticket, Edit, Mail, Gift, FileText, Trophy, Train, Building2
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { AdminMobileNav } from "@/components/admin/AdminMobileNav";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
  DropdownMenuSeparator,
} from "@/components/ui/dropdown-menu";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import { toast } from "sonner";
import BookingCalendar from "@/components/admin/BookingCalendar";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const AdminDashboard = () => {
  const navigate = useNavigate();
  const [bookings, setBookings] = useState([]);
  const [stats, setStats] = useState(null);
  const [visitorStats, setVisitorStats] = useState(null);
  const [loginHistory, setLoginHistory] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filters, setFilters] = useState({ zone: "all", status: "all" });
  const [selectedDay, setSelectedDay] = useState(null);
  const [selectedDayBookings, setSelectedDayBookings] = useState([]);
  const [showVisitorHistory, setShowVisitorHistory] = useState(false);
  const [showLoginHistory, setShowLoginHistory] = useState(false);
  
  // Edit booking modal state
  const [editingBooking, setEditingBooking] = useState(null);
  const [availableSlots, setAvailableSlots] = useState([]);
  const [loadingSlots, setLoadingSlots] = useState(false);
  const [slotType, setSlotType] = useState("delivery"); // "pickup" or "delivery"

  const getAuth = useCallback(() => {
    const auth = localStorage.getItem("adminAuth");
    if (!auth) {
      navigate("/admin");
      return null;
    }
    return { Authorization: `Basic ${auth}` };
  }, [navigate]);

  const fetchData = useCallback(async () => {
    const headers = getAuth();
    if (!headers) return;

    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (filters.zone !== "all") params.append("zone_id", filters.zone);
      if (filters.status !== "all") params.append("status", filters.status);

      const [bookingsRes, statsRes, visitorRes, loginHistoryRes] = await Promise.all([
        axios.get(`${API}/admin/bookings?${params.toString()}`, { headers }),
        axios.get(`${API}/admin/stats`, { headers }),
        axios.get(`${API}/admin/visitor-stats`, { headers }),
        axios.get(`${API}/admin/login-history`, { headers }),
      ]);

      setBookings(bookingsRes.data);
      setStats(statsRes.data);
      setVisitorStats(visitorRes.data);
      setLoginHistory(loginHistoryRes.data.logins || []);
    } catch (error) {
      console.error("Error fetching data:", error);
      if (error.response?.status === 401) {
        localStorage.removeItem("adminAuth");
        navigate("/admin");
      }
      toast.error("Erreur lors du chargement des données");
    } finally {
      setLoading(false);
    }
  }, [filters, getAuth, navigate]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const updateBookingStatus = async (bookingId, newStatus) => {
    const headers = getAuth();
    if (!headers) return;

    try {
      await axios.put(
        `${API}/admin/bookings/${bookingId}`,
        { status: newStatus },
        { headers }
      );
      toast.success("Statut mis à jour");
      fetchData();
    } catch (error) {
      console.error("Error updating status:", error);
      toast.error("Erreur lors de la mise à jour");
    }
  };

  // Open edit modal and fetch available slots
  const openEditBooking = async (booking, type) => {
    setEditingBooking(booking);
    setSlotType(type);
    setLoadingSlots(true);
    setAvailableSlots([]);
    
    const headers = getAuth();
    if (!headers) {
      setLoadingSlots(false);
      return;
    }
    
    try {
      const today = new Date();
      const endDate = addDays(today, 60);
      
      console.log("Fetching slots for zone:", booking.zone_id);
      const response = await axios.get(`${API}/slots/${booking.zone_id}`);
      console.log("Slots response:", response.data);
      
      // Filter slots with availability and within 60 days
      const slotsWithAvailability = (response.data || []).filter(slot => {
        const slotDate = new Date(slot.date);
        return slot.current_bookings < slot.max_bookings && 
               slot.is_active && 
               slotDate <= endDate;
      });
      
      // Sort by date
      slotsWithAvailability.sort((a, b) => new Date(a.date) - new Date(b.date));
      
      setAvailableSlots(slotsWithAvailability);
      
      if (slotsWithAvailability.length === 0) {
        toast.info("Aucun créneau disponible dans les 60 prochains jours pour cette zone");
      }
    } catch (error) {
      console.error("Error fetching slots:", error);
      toast.error("Erreur lors du chargement des créneaux: " + (error.response?.data?.detail || error.message));
    } finally {
      setLoadingSlots(false);
    }
  };
  
  // Update booking slot
  const updateBookingSlot = async (newSlotId, newSlot) => {
    if (!editingBooking) return;
    
    const headers = getAuth();
    if (!headers) return;
    
    try {
      const updateData = {};
      if (slotType === "delivery") {
        updateData.delivery_slot_id = newSlotId;
      } else {
        updateData.pickup_slot_id = newSlotId;
      }
      
      await axios.put(
        `${API}/admin/bookings/${editingBooking.id}`,
        updateData,
        { headers }
      );
      
      toast.success(`Créneau de ${slotType === "delivery" ? "livraison" : "ramassage"} modifié`);
      setEditingBooking(null);
      fetchData();
    } catch (error) {
      console.error("Error updating slot:", error);
      toast.error(error.response?.data?.detail || "Erreur lors de la modification");
    }
  };

  const deleteCancelledBookings = async () => {
    const headers = getAuth();
    if (!headers) return;

    try {
      const response = await axios.delete(`${API}/admin/bookings/cancelled/all`, { headers });
      toast.success(response.data.message);
      fetchData();
    } catch (error) {
      console.error("Error deleting cancelled bookings:", error);
      toast.error("Erreur lors de la suppression");
    }
  };

  const deleteDeliveredBookings = async () => {
    const headers = getAuth();
    if (!headers) return;

    try {
      const response = await axios.delete(`${API}/admin/bookings/delivered/all`, { headers });
      toast.success(response.data.message);
      fetchData();
    } catch (error) {
      console.error("Error deleting delivered bookings:", error);
      toast.error("Erreur lors de la suppression");
    }
  };

  const cancelledCount = bookings.filter(b => b.status === "cancelled").length;
  const deliveredCount = bookings.filter(b => b.status === "delivered").length;

  const handleDayClick = (dateKey, dayBookings) => {
    setSelectedDay(dateKey);
    setSelectedDayBookings(dayBookings);
  };

  const closeDayModal = () => {
    setSelectedDay(null);
    setSelectedDayBookings([]);
  };

  const handleLogout = () => {
    localStorage.removeItem("adminAuth");
    navigate("/admin");
  };

  const getStatusBadge = (status) => {
    const variants = {
      pending: { class: "status-pending", label: "En attente" },
      confirmed: { class: "status-confirmed", label: "Confirmé" },
      delivered: { class: "status-delivered", label: "Livré" },
      cancelled: { class: "status-cancelled", label: "Annulé" },
    };
    const variant = variants[status] || variants.pending;
    return <Badge className={variant.class}>{variant.label}</Badge>;
  };

  const getZoneName = (zoneId) => {
    const zones = {
      "lyon": "Lyon",
      "saint-rambert": "Saint-Rambert",
      "francheville": "Francheville"
    };
    return zones[zoneId] || zoneId;
  };

  return (
    <div className="min-h-screen bg-slate-50 flex">
      {/* Sidebar */}
      <aside className="admin-sidebar hidden md:block sticky top-0 h-screen overflow-y-auto">
        <div className="flex items-center justify-center mb-10">
          <img 
            src="https://customer-assets.emergentagent.com/job_pressingexpress/artifacts/d4qg5zqd_Capture%20d%E2%80%99%C3%A9cran%202025-01-06%20102414.png" 
            alt="Pressing Sakura" 
            className="h-16 w-auto bg-white rounded-lg p-1"
          />
        </div>

        <nav className="space-y-2">
          <div className="admin-nav-item active" data-testid="nav-dashboard">
            <LayoutDashboard className="w-5 h-5" />
            <span>Tableau de bord</span>
          </div>
          <div
            className="admin-nav-item"
            onClick={() => navigate("/admin/planning")}
            data-testid="nav-planning"
          >
            <Calendar className="w-5 h-5" />
            <span>Planning</span>
          </div>
          <div
            className="admin-nav-item"
            onClick={() => navigate("/admin/ticket")}
            data-testid="nav-ticket"
          >
            <Ticket className="w-5 h-5" />
            <span>Ticket</span>
          </div>
          <div
            className="admin-nav-item"
            onClick={() => navigate("/admin/slots")}
            data-testid="nav-slots"
          >
            <Clock className="w-5 h-5" />
            <span>Gérer les créneaux</span>
          </div>
          <div
            className="admin-nav-item"
            onClick={() => navigate("/admin/customers")}
            data-testid="nav-customers"
          >
            <Users className="w-5 h-5" />
            <span>Clients</span>
          </div>
          <div
            className="admin-nav-item"
            onClick={() => navigate("/admin/customers?filter=pro")}
            data-testid="nav-customers-pro"
          >
            <Building className="w-5 h-5" />
            <span>Clients Pro</span>
          </div>
          <div
            className="admin-nav-item"
            onClick={() => navigate("/admin/sakura")}
            data-testid="nav-sakura"
          >
            <Bot className="w-5 h-5" />
            <span>IA Sakura</span>
          </div>
          <div
            className="admin-nav-item"
            onClick={() => navigate("/admin/email")}
            data-testid="nav-email"
          >
            <Mail className="w-5 h-5" />
            <span>Email masse</span>
          </div>
          <div
            className="admin-nav-item"
            onClick={() => navigate("/admin/referrals")}
            data-testid="nav-referrals"
          >
            <Gift className="w-5 h-5" />
            <span>Parrainages</span>
          </div>
          <div
            className="admin-nav-item"
            onClick={() => navigate("/admin/delivery-slips")}
            data-testid="nav-delivery-slips"
          >
            <FileText className="w-5 h-5" />
            <span>Bons Livraison</span>
          </div>
          <div
            className="admin-nav-item"
            onClick={() => navigate("/admin/snake-leaderboard")}
            data-testid="nav-snake-leaderboard"
          >
            <Trophy className="w-5 h-5" />
            <span>Podium Snake</span>
          </div>
          <div
            className="admin-nav-item"
            onClick={() => navigate("/admin/chat-history")}
            data-testid="nav-chat-history"
          >
            <Bot className="w-5 h-5" />
            <span>Historique IA</span>
          </div>
          <div
            className="admin-nav-item"
            onClick={() => navigate("/admin/trenitalia")}
            data-testid="nav-trenitalia"
          >
            <Train className="w-5 h-5" />
            <span>Trenitalia</span>
          </div>
          <div
            className="admin-nav-item"
            onClick={() => navigate("/admin/conciergerie")}
            data-testid="nav-conciergerie"
          >
            <Building2 className="w-5 h-5" />
            <span>Conciergerie</span>
          </div>
        </nav>

        <div className="mt-6 pt-4 border-t border-slate-800">
          <button
            onClick={handleLogout}
            className="admin-nav-item text-slate-400 hover:text-white"
            data-testid="logout-btn"
          >
            <LogOut className="w-5 h-5" />
            <span>Déconnexion</span>
          </button>
        </div>
      </aside>

      {/* Main Content */}
      <main className="flex-1 p-6 md:p-10 overflow-auto pb-20 md:pb-10">
        {/* Mobile Header with Navigation */}
        <div className="md:hidden mb-6">
          <div className="flex items-center justify-between mb-4">
            <h1 className="text-xl font-bold" style={{ fontFamily: 'Outfit, sans-serif' }}>
              Dashboard
            </h1>
            <Button variant="ghost" size="icon" onClick={handleLogout}>
              <LogOut className="w-5 h-5" />
            </Button>
          </div>
          <AdminMobileNav />
        </div>

        {/* Action Buttons */}
        <div className="flex flex-wrap gap-3 mb-6">
          <Button 
            className="btn-primary"
            onClick={() => navigate("/admin/booking/new")}
            data-testid="new-booking-btn"
          >
            <User className="w-4 h-4 mr-2" />
            Nouvelle réservation
          </Button>
          <Button 
            variant="outline"
            onClick={() => navigate("/admin/planning")}
            data-testid="planning-btn"
          >
            <Calendar className="w-4 h-4 mr-2" />
            Voir le planning
          </Button>
        </div>

        {/* Stats Cards */}
        {stats && (
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
            <Card>
              <CardContent className="p-4">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 bg-blue-100 rounded-lg flex items-center justify-center">
                    <Package className="w-5 h-5 text-blue-600" />
                  </div>
                  <div>
                    <p className="text-sm text-slate-500">Total</p>
                    <p className="text-2xl font-bold">{stats.total_bookings}</p>
                  </div>
                </div>
              </CardContent>
            </Card>

            <Card>
              <CardContent className="p-4">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 bg-amber-100 rounded-lg flex items-center justify-center">
                    <Clock className="w-5 h-5 text-amber-600" />
                  </div>
                  <div>
                    <p className="text-sm text-slate-500">En attente</p>
                    <p className="text-2xl font-bold">{stats.pending}</p>
                  </div>
                </div>
              </CardContent>
            </Card>

            <Card>
              <CardContent className="p-4">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 bg-blue-100 rounded-lg flex items-center justify-center">
                    <CheckCircle className="w-5 h-5 text-blue-600" />
                  </div>
                  <div>
                    <p className="text-sm text-slate-500">Confirmés</p>
                    <p className="text-2xl font-bold">{stats.confirmed}</p>
                  </div>
                </div>
              </CardContent>
            </Card>

            <Card>
              <CardContent className="p-4">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 bg-green-100 rounded-lg flex items-center justify-center">
                    <Truck className="w-5 h-5 text-green-600" />
                  </div>
                  <div>
                    <p className="text-sm text-slate-500">Livrés</p>
                    <p className="text-2xl font-bold">{stats.delivered}</p>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>
        )}

        {/* Visitor Stats */}
        {visitorStats && (
          <div className="mb-8">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-lg font-semibold flex items-center gap-2">
                <Eye className="w-5 h-5 text-pink-500" />
                Statistiques visiteurs
              </h2>
              <Button 
                variant="outline" 
                size="sm"
                onClick={() => setShowVisitorHistory(!showVisitorHistory)}
              >
                {showVisitorHistory ? "Masquer" : "Voir"} l'historique
              </Button>
            </div>
            
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              {/* Today's home visitors */}
              <Card className="border-pink-200 bg-pink-50/50">
                <CardContent className="p-4">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 bg-pink-100 rounded-lg flex items-center justify-center">
                      <Eye className="w-5 h-5 text-pink-600" />
                    </div>
                    <div>
                      <p className="text-sm text-slate-500">Visiteurs aujourd'hui</p>
                      <p className="text-2xl font-bold text-pink-600">{visitorStats.today?.home?.unique || 0}</p>
                      <p className="text-xs text-slate-400">{visitorStats.today?.home?.total || 0} visites</p>
                    </div>
                  </div>
                </CardContent>
              </Card>

              {/* Today's client logins */}
              <Card className="border-purple-200 bg-purple-50/50">
                <CardContent className="p-4">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 bg-purple-100 rounded-lg flex items-center justify-center">
                      <UserCheck className="w-5 h-5 text-purple-600" />
                    </div>
                    <div>
                      <p className="text-sm text-slate-500">Connexions clients</p>
                      <p className="text-2xl font-bold text-purple-600">{visitorStats.today?.client_login?.unique || 0}</p>
                      <p className="text-xs text-slate-400">{visitorStats.today?.client_login?.total || 0} connexions</p>
                    </div>
                  </div>
                </CardContent>
              </Card>

              {/* Month total visitors */}
              <Card>
                <CardContent className="p-4">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 bg-slate-100 rounded-lg flex items-center justify-center">
                      <Eye className="w-5 h-5 text-slate-600" />
                    </div>
                    <div>
                      <p className="text-sm text-slate-500">Visiteurs (30j)</p>
                      <p className="text-2xl font-bold">{visitorStats.month_totals?.home?.unique || 0}</p>
                      <p className="text-xs text-slate-400">{visitorStats.month_totals?.home?.total || 0} visites</p>
                    </div>
                  </div>
                </CardContent>
              </Card>

              {/* Month total logins */}
              <Card>
                <CardContent className="p-4">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 bg-slate-100 rounded-lg flex items-center justify-center">
                      <UserCheck className="w-5 h-5 text-slate-600" />
                    </div>
                    <div>
                      <p className="text-sm text-slate-500">Connexions (30j)</p>
                      <p className="text-2xl font-bold">{visitorStats.month_totals?.client_login?.unique || 0}</p>
                      <p className="text-xs text-slate-400">{visitorStats.month_totals?.client_login?.total || 0} connexions</p>
                    </div>
                  </div>
                </CardContent>
              </Card>
            </div>

            {/* History Table */}
            {showVisitorHistory && visitorStats.history && visitorStats.history.length > 0 && (
              <Card className="mt-4">
                <CardHeader className="pb-2">
                  <CardTitle className="text-base">Historique des 30 derniers jours</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="max-h-64 overflow-y-auto">
                    <table className="w-full text-sm">
                      <thead className="sticky top-0 bg-white">
                        <tr className="border-b">
                          <th className="text-left py-2 px-2">Date</th>
                          <th className="text-center py-2 px-2">Visiteurs uniques</th>
                          <th className="text-center py-2 px-2">Visites totales</th>
                          <th className="text-center py-2 px-2">Connexions clients</th>
                        </tr>
                      </thead>
                      <tbody>
                        {[...visitorStats.history].reverse().map((day) => (
                          <tr key={day.date} className="border-b hover:bg-slate-50">
                            <td className="py-2 px-2 font-medium">
                              {format(new Date(day.date), "dd/MM/yyyy", { locale: fr })}
                            </td>
                            <td className="text-center py-2 px-2">
                              <Badge variant="outline" className="bg-pink-50 text-pink-600 border-pink-200">
                                {day.home?.unique || 0}
                              </Badge>
                            </td>
                            <td className="text-center py-2 px-2 text-slate-500">
                              {day.home?.total || 0}
                            </td>
                            <td className="text-center py-2 px-2">
                              <Badge variant="outline" className="bg-purple-50 text-purple-600 border-purple-200">
                                {day.client_login?.unique || 0}
                              </Badge>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </CardContent>
              </Card>
            )}

            {/* Login History Detail */}
            <div className="mt-4">
              <Button 
                variant="outline" 
                size="sm"
                onClick={() => setShowLoginHistory(!showLoginHistory)}
                className="mb-3"
              >
                <UserCheck className="w-4 h-4 mr-2" />
                {showLoginHistory ? "Masquer" : "Voir"} le détail des connexions clients
              </Button>
              
              {showLoginHistory && loginHistory.length > 0 && (
                <Card>
                  <CardHeader className="pb-2">
                    <CardTitle className="text-base flex items-center gap-2">
                      <UserCheck className="w-4 h-4 text-purple-500" />
                      Historique des connexions (100 dernières)
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="max-h-80 overflow-y-auto">
                      <table className="w-full text-sm">
                        <thead className="sticky top-0 bg-white">
                          <tr className="border-b">
                            <th className="text-left py-2 px-2">Date/Heure</th>
                            <th className="text-left py-2 px-2">Client</th>
                            <th className="text-left py-2 px-2">Email</th>
                            <th className="text-center py-2 px-2">Visites</th>
                          </tr>
                        </thead>
                        <tbody>
                          {loginHistory.map((login, idx) => (
                            <tr key={idx} className="border-b hover:bg-purple-50/50">
                              <td className="py-2 px-2 text-slate-600">
                                {login.time ? format(new Date(login.time), "dd/MM/yyyy HH:mm", { locale: fr }) : login.date}
                              </td>
                              <td className="py-2 px-2 font-medium text-purple-700">
                                {login.customer_name}
                              </td>
                              <td className="py-2 px-2 text-slate-500 text-xs">
                                {login.customer_email}
                              </td>
                              <td className="text-center py-2 px-2">
                                <Badge variant="outline" className="bg-purple-50 text-purple-600 border-purple-200">
                                  {login.visit_count}
                                </Badge>
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </CardContent>
                </Card>
              )}
              
              {showLoginHistory && loginHistory.length === 0 && (
                <Card>
                  <CardContent className="p-6 text-center text-slate-500">
                    Aucune connexion client enregistrée pour le moment.
                    <br />
                    <span className="text-xs">Les nouvelles connexions apparaîtront ici avec le nom et l'email du client.</span>
                  </CardContent>
                </Card>
              )}
            </div>
          </div>
        )}

        {/* Calendar View */}
        <BookingCalendar 
          bookings={bookings} 
          onDayClick={handleDayClick}
        />

        {/* Day Detail Modal */}
        {selectedDay && (
          <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
            <div className="bg-white rounded-xl w-full max-w-2xl max-h-[80vh] overflow-hidden shadow-2xl">
              <div className="flex items-center justify-between p-4 border-b bg-gradient-to-r from-pink-50 to-pink-100">
                <h3 className="font-bold text-lg">
                  📅 {format(new Date(selectedDay), "EEEE d MMMM yyyy", { locale: fr })}
                </h3>
                <Button variant="ghost" size="icon" onClick={closeDayModal}>
                  <X className="w-5 h-5" />
                </Button>
              </div>
              <div className="p-4 overflow-y-auto max-h-[60vh]">
                {selectedDayBookings.length === 0 ? (
                  <p className="text-center text-slate-500 py-8">Aucune réservation ce jour</p>
                ) : (
                  <div className="space-y-3">
                    {selectedDayBookings.map((booking, idx) => (
                      <div 
                        key={`${booking.id}-${idx}`}
                        className={`p-4 rounded-lg border ${
                          booking.type === 'combined' 
                            ? 'bg-purple-50 border-purple-200' 
                            : booking.type === 'pickup' 
                              ? 'bg-pink-50 border-pink-200' 
                              : 'bg-green-50 border-green-200'
                        }`}
                      >
                        <div className="flex items-start justify-between">
                          <div className="flex items-center gap-2 mb-2">
                            {booking.type === 'combined' ? (
                              <>
                                <Package className="w-4 h-4 text-pink-600" />
                                <span className="text-purple-600">+</span>
                                <Truck className="w-4 h-4 text-green-600" />
                              </>
                            ) : booking.type === 'pickup' ? (
                              <Package className="w-5 h-5 text-pink-600" />
                            ) : (
                              <Truck className="w-5 h-5 text-green-600" />
                            )}
                            <span className={`font-semibold ${
                              booking.type === 'combined' 
                                ? 'text-purple-700' 
                                : booking.type === 'pickup' 
                                  ? 'text-pink-700' 
                                  : 'text-green-700'
                            }`}>
                              {booking.type === 'combined' 
                                ? 'Ramassage + Livraison' 
                                : booking.type === 'pickup' 
                                  ? 'Ramassage' 
                                  : 'Livraison'}
                            </span>
                            {getStatusBadge(booking.status)}
                            {booking.is_recurring && (
                              <Badge className="bg-purple-100 text-purple-700 text-xs">
                                <Repeat className="w-3 h-3 mr-1" />
                                Récurrent
                              </Badge>
                            )}
                          </div>
                          <div className="text-sm text-slate-500 text-right">
                            {booking.type === 'combined' ? (
                              <div>
                                <div>📦 {booking.pickup_time}</div>
                                <div>🚚 {booking.delivery_time}</div>
                              </div>
                            ) : booking.type === 'pickup' ? (
                              booking.pickup_time
                            ) : (
                              booking.delivery_time
                            )}
                          </div>
                        </div>
                        <div className="ml-7">
                          <p className="font-medium">{booking.first_name} {booking.last_name}</p>
                          <p className="text-sm text-slate-600">{booking.address}</p>
                          {booking.phone && (
                            <p className="text-sm text-slate-500">{booking.phone}</p>
                          )}
                          {booking.remarks && (
                            <p className="text-sm text-amber-600 mt-1">💬 {booking.remarks}</p>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
              <div className="p-4 border-t flex justify-between items-center">
                <span className="text-sm text-slate-500">
                  {selectedDayBookings.length} passage(s)
                </span>
                <Button onClick={() => {
                  navigate(`/admin/planning?date=${selectedDay}`);
                  closeDayModal();
                }}>
                  Voir le planning détaillé
                </Button>
              </div>
            </div>
          </div>
        )}

        {/* Bookings Table */}
        <Card>
          <CardHeader className="flex flex-row items-center justify-between flex-wrap gap-4">
            <CardTitle style={{ fontFamily: 'Outfit, sans-serif' }}>
              Réservations
            </CardTitle>
            <div className="flex items-center gap-3 flex-wrap">
              {cancelledCount > 0 && (
                <AlertDialog>
                  <AlertDialogTrigger asChild>
                    <Button
                      variant="outline"
                      size="sm"
                      className="text-red-600 border-red-200 hover:bg-red-50"
                      data-testid="delete-cancelled-btn"
                    >
                      <Trash2 className="w-4 h-4 mr-2" />
                      Vider annulées ({cancelledCount})
                    </Button>
                  </AlertDialogTrigger>
                  <AlertDialogContent>
                    <AlertDialogHeader>
                      <AlertDialogTitle>Supprimer les réservations annulées ?</AlertDialogTitle>
                      <AlertDialogDescription>
                        Cette action va supprimer définitivement {cancelledCount} réservation(s) annulée(s). 
                        Cette action est irréversible.
                      </AlertDialogDescription>
                    </AlertDialogHeader>
                    <AlertDialogFooter>
                      <AlertDialogCancel>Annuler</AlertDialogCancel>
                      <AlertDialogAction 
                        onClick={deleteCancelledBookings}
                        className="bg-red-600 hover:bg-red-700"
                      >
                        Supprimer
                      </AlertDialogAction>
                    </AlertDialogFooter>
                  </AlertDialogContent>
                </AlertDialog>
              )}

              {deliveredCount > 0 && (
                <AlertDialog>
                  <AlertDialogTrigger asChild>
                    <Button
                      variant="outline"
                      size="sm"
                      className="text-green-600 border-green-200 hover:bg-green-50"
                      data-testid="delete-delivered-btn"
                    >
                      <Trash2 className="w-4 h-4 mr-2" />
                      Vider livrées ({deliveredCount})
                    </Button>
                  </AlertDialogTrigger>
                  <AlertDialogContent>
                    <AlertDialogHeader>
                      <AlertDialogTitle>Supprimer les réservations livrées ?</AlertDialogTitle>
                      <AlertDialogDescription>
                        Cette action va supprimer définitivement {deliveredCount} réservation(s) livrée(s). 
                        Cette action est irréversible.
                      </AlertDialogDescription>
                    </AlertDialogHeader>
                    <AlertDialogFooter>
                      <AlertDialogCancel>Annuler</AlertDialogCancel>
                      <AlertDialogAction 
                        onClick={deleteDeliveredBookings}
                        className="bg-green-600 hover:bg-green-700"
                      >
                        Supprimer
                      </AlertDialogAction>
                    </AlertDialogFooter>
                  </AlertDialogContent>
                </AlertDialog>
              )}
              
              <Button
                variant="outline"
                size="icon"
                onClick={fetchData}
                data-testid="refresh-btn"
              >
                <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
              </Button>
              
              <Select
                value={filters.zone}
                onValueChange={(value) => setFilters(prev => ({ ...prev, zone: value }))}
              >
                <SelectTrigger className="w-40" data-testid="filter-zone">
                  <MapPin className="w-4 h-4 mr-2" />
                  <SelectValue placeholder="Zone" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Toutes zones</SelectItem>
                  <SelectItem value="lyon">Lyon</SelectItem>
                  <SelectItem value="saint-rambert">Saint-Rambert</SelectItem>
                  <SelectItem value="francheville">Francheville</SelectItem>
                </SelectContent>
              </Select>

              <Select
                value={filters.status}
                onValueChange={(value) => setFilters(prev => ({ ...prev, status: value }))}
              >
                <SelectTrigger className="w-40" data-testid="filter-status">
                  <Filter className="w-4 h-4 mr-2" />
                  <SelectValue placeholder="Statut" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Tous statuts</SelectItem>
                  <SelectItem value="pending">En attente</SelectItem>
                  <SelectItem value="confirmed">Confirmé</SelectItem>
                  <SelectItem value="delivered">Livré</SelectItem>
                  <SelectItem value="cancelled">Annulé</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </CardHeader>
          <CardContent>
            {loading ? (
              <div className="text-center py-12">
                <div className="animate-spin w-8 h-8 border-2 border-primary border-t-transparent rounded-full mx-auto"></div>
              </div>
            ) : bookings.length === 0 ? (
              <div className="text-center py-12">
                <Package className="w-12 h-12 text-slate-300 mx-auto mb-4" />
                <p className="text-slate-500">Aucune réservation trouvée</p>
              </div>
            ) : (
              <div className="overflow-x-auto">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Client</TableHead>
                      <TableHead>Zone</TableHead>
                      <TableHead>Date / Créneau</TableHead>
                      <TableHead>Adresse</TableHead>
                      <TableHead>Statut</TableHead>
                      <TableHead>Actions</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {bookings.map((booking) => (
                      <TableRow key={booking.id} data-testid={`booking-row-${booking.id}`}>
                        <TableCell>
                          <div className="flex items-center gap-2">
                            <User className="w-4 h-4 text-slate-400" />
                            <div>
                              <div className="flex items-center gap-2">
                                <button
                                  type="button"
                                  onClick={() => navigate(`/admin/customers?search=${encodeURIComponent(booking.email || booking.phone || `${booking.first_name} ${booking.last_name}`)}`)}
                                  className="font-medium text-left text-pink-600 hover:text-pink-800 hover:underline transition-colors cursor-pointer"
                                  title="Voir la fiche client"
                                  data-testid={`client-link-${booking.id}`}
                                >
                                  {booking.first_name} {booking.last_name}
                                </button>
                                {booking.is_vip && (
                                  <Badge className="bg-gradient-to-r from-amber-400 to-yellow-500 text-white text-xs px-1.5 py-0.5 flex items-center gap-1" title="Réservation prioritaire">
                                    <Crown className="w-3 h-3" />
                                    VIP
                                  </Badge>
                                )}
                              </div>
                              {booking.phone && (
                                <p className="text-sm text-slate-500">{booking.phone}</p>
                              )}
                            </div>
                          </div>
                        </TableCell>
                        <TableCell>
                          <Badge variant="outline">{getZoneName(booking.zone_id)}</Badge>
                          {booking.is_recurring && (
                            <Badge className="ml-1 bg-purple-100 text-purple-700 text-xs">
                              <Repeat className="w-3 h-3 mr-1" />
                              Récurrent
                            </Badge>
                          )}
                        </TableCell>
                        <TableCell>
                          <div className="space-y-2">
                            {/* Pickup slot */}
                            {booking.pickup_date && (
                              <div className="flex items-start gap-2">
                                <Package className="w-4 h-4 text-pink-500 mt-0.5 flex-shrink-0" />
                                <div>
                                  <p className="text-xs text-slate-400 uppercase">Ramassage</p>
                                  <p className="font-medium text-sm">
                                    {format(new Date(booking.pickup_date), "d MMM", { locale: fr })}
                                  </p>
                                  <p className="text-xs text-slate-500">{booking.pickup_time}</p>
                                </div>
                              </div>
                            )}
                            
                            {/* Delivery slot */}
                            {booking.delivery_date && (
                              <div className="flex items-start gap-2">
                                <Truck className="w-4 h-4 text-green-500 mt-0.5 flex-shrink-0" />
                                <div>
                                  <p className="text-xs text-slate-400 uppercase">Livraison</p>
                                  <p className="font-medium text-sm">
                                    {format(new Date(booking.delivery_date), "d MMM", { locale: fr })}
                                  </p>
                                  <p className="text-xs text-slate-500">{booking.delivery_time}</p>
                                </div>
                              </div>
                            )}
                            
                            {/* Legacy model fallback */}
                            {!booking.pickup_date && !booking.delivery_date && booking.slot_date && (
                              <div>
                                <p className="font-medium">
                                  {format(new Date(booking.slot_date), "d MMM yyyy", { locale: fr })}
                                </p>
                                <p className="text-sm text-slate-500">{booking.slot_time}</p>
                              </div>
                            )}
                            
                            {/* No slot info */}
                            {!booking.pickup_date && !booking.delivery_date && !booking.slot_date && (
                              <p className="text-slate-400 text-sm">-</p>
                            )}
                          </div>
                          
                          {booking.has_surcharge && (
                            <Badge className="mt-2 bg-amber-100 text-amber-700 hover:bg-amber-100">
                              Express +{booking.surcharge_percent || 50}%
                            </Badge>
                          )}
                        </TableCell>
                        <TableCell className="max-w-xs">
                          <p className="truncate">{booking.address}</p>
                          {booking.email && (
                            <p className="text-xs text-slate-500 mt-1 truncate">
                              <a href={`mailto:${booking.email}`} className="hover:text-primary">
                                📧 {booking.email}
                              </a>
                            </p>
                          )}
                          {booking.remarks && (
                            <p className="text-xs text-slate-500 mt-1 italic truncate" title={booking.remarks}>
                              📝 {booking.remarks}
                            </p>
                          )}
                        </TableCell>
                        <TableCell>{getStatusBadge(booking.status)}</TableCell>
                        <TableCell>
                          <DropdownMenu>
                            <DropdownMenuTrigger asChild>
                              <Button variant="ghost" size="sm" data-testid={`action-btn-${booking.id}`}>
                                Actions
                              </Button>
                            </DropdownMenuTrigger>
                            <DropdownMenuContent align="end">
                              {/* Slot modification options */}
                              {booking.pickup_slot_id && (
                                <DropdownMenuItem
                                  onClick={() => openEditBooking(booking, "pickup")}
                                >
                                  <Edit className="w-4 h-4 mr-2 text-orange-600" />
                                  Changer créneau ramassage
                                </DropdownMenuItem>
                              )}
                              {booking.delivery_slot_id && (
                                <DropdownMenuItem
                                  onClick={() => openEditBooking(booking, "delivery")}
                                >
                                  <Edit className="w-4 h-4 mr-2 text-blue-600" />
                                  Changer créneau livraison
                                </DropdownMenuItem>
                              )}
                              <DropdownMenuSeparator />
                              {/* Status options */}
                              <DropdownMenuItem
                                onClick={() => updateBookingStatus(booking.id, "confirmed")}
                              >
                                <CheckCircle className="w-4 h-4 mr-2 text-blue-600" />
                                Confirmer
                              </DropdownMenuItem>
                              <DropdownMenuItem
                                onClick={() => updateBookingStatus(booking.id, "delivered")}
                              >
                                <Truck className="w-4 h-4 mr-2 text-green-600" />
                                Marquer livré
                              </DropdownMenuItem>
                              <DropdownMenuItem
                                onClick={() => updateBookingStatus(booking.id, "cancelled")}
                                className="text-red-600"
                              >
                                Annuler
                              </DropdownMenuItem>
                            </DropdownMenuContent>
                          </DropdownMenu>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Mobile Navigation */}
        <div className="md:hidden fixed bottom-0 left-0 right-0 bg-white border-t border-slate-200 p-4">
          <div className="flex justify-around">
            <button className="flex flex-col items-center text-primary">
              <LayoutDashboard className="w-5 h-5" />
              <span className="text-xs mt-1">Dashboard</span>
            </button>
            <button
              className="flex flex-col items-center text-slate-500"
              onClick={() => navigate("/admin/planning")}
            >
              <Calendar className="w-5 h-5" />
              <span className="text-xs mt-1">Planning</span>
            </button>
            <button
              className="flex flex-col items-center text-slate-500"
              onClick={() => navigate("/admin/slots")}
            >
              <Clock className="w-5 h-5" />
              <span className="text-xs mt-1">Créneaux</span>
            </button>
          </div>
        </div>
      </main>
      
      {/* Edit Booking Slot Modal */}
      {editingBooking && (
        <div className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4">
          <Card className="w-full max-w-lg max-h-[80vh] overflow-hidden">
            <CardHeader className="border-b">
              <CardTitle className="flex items-center gap-2">
                <Edit className="w-5 h-5 text-blue-500" />
                Changer le créneau de {slotType === "delivery" ? "livraison" : "ramassage"}
              </CardTitle>
              <p className="text-sm text-slate-500">
                {editingBooking.first_name} {editingBooking.last_name} - {editingBooking.zone_id === "lyon" ? "Lyon" : "Saint-Rambert"}
              </p>
            </CardHeader>
            <CardContent className="p-4">
              {loadingSlots ? (
                <div className="text-center py-8">
                  <div className="animate-spin w-8 h-8 border-4 border-pink-500 border-t-transparent rounded-full mx-auto mb-2"></div>
                  <p className="text-slate-500">Chargement des créneaux...</p>
                </div>
              ) : availableSlots.length === 0 ? (
                <div className="text-center py-8 text-slate-500">
                  Aucun créneau disponible dans les 60 prochains jours
                </div>
              ) : (
                <div className="space-y-2 max-h-96 overflow-y-auto">
                  {availableSlots.map(slot => (
                    <div
                      key={slot.id}
                      className={`flex items-center justify-between p-3 border rounded-lg hover:bg-slate-50 cursor-pointer ${
                        (slotType === "delivery" && slot.id === editingBooking.delivery_slot_id) ||
                        (slotType === "pickup" && slot.id === editingBooking.pickup_slot_id)
                          ? "bg-blue-50 border-blue-300"
                          : ""
                      }`}
                      onClick={() => updateBookingSlot(slot.id, slot)}
                    >
                      <div>
                        <p className="font-medium">
                          {format(new Date(slot.date), "EEEE d MMMM yyyy", { locale: fr })}
                        </p>
                        <p className="text-sm text-slate-500">
                          {slot.start_time} - {slot.end_time}
                        </p>
                      </div>
                      <div className="flex items-center gap-2">
                        <Badge variant="outline" className="text-xs">
                          {slot.max_bookings - slot.current_bookings} place(s)
                        </Badge>
                        {((slotType === "delivery" && slot.id === editingBooking.delivery_slot_id) ||
                          (slotType === "pickup" && slot.id === editingBooking.pickup_slot_id)) && (
                          <Badge className="bg-blue-500">Actuel</Badge>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
            <div className="border-t p-4">
              <Button
                variant="outline"
                className="w-full"
                onClick={() => setEditingBooking(null)}
              >
                Fermer
              </Button>
            </div>
          </Card>
        </div>
      )}
    </div>
  );
};

export default AdminDashboard;
